import { TeamWith } from "@redotech/account-common/base-team";
import { SettingsPermissions } from "@redotech/account-common/permissions";
import { Payer } from "@redotech/congo-enums/payer";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { AdvancedExchanges } from "@redotech/redo-return-core-common/exchange-settings";
import type { ReturnState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  type AdjustmentAmounts,
  adjustmentAmountsForUnit,
  formatFlatAmount,
  hasConflictingAdjustmentUnits,
} from "@redotech/redo-return-core-common/return-flow/adjustment-unit";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { StoreCreditType } from "@redotech/redo-return-core-common/return-flow/return-option-enums";
import { RequiredMessage } from "@redotech/redo-return-ui/flows/required-message";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RETURNS_AUTOMATIONS_SETTINGS_PATH } from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { Team } from "@redotech/redo-team-common/team";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { CollapseSubsection } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import { Link } from "@redotech/redo-web/link";
import {
  ProductAutocomplete,
  ProductVariantInfo,
} from "@redotech/redo-web/product-autocomplete";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useContext, useState } from "react";
import { useFieldEditable } from "../flow-editor/flow-readonly-context";
import { AdvancedReturnOptions } from "./advanced-return-options";
import { AmountType, AmountTypeDropdown } from "./amount-type-dropdown";
import { useProductAutocomplete } from "./hooks/use-product-autocomplete";
import { PerCurrencyOverrides } from "./per-currency-overrides";
import * as returnCss from "./return.module.css";
import { isBonusAdjustment } from "./util";

export const returnOptionMethods: ReturnOptionMethod["type"][] = [
  ReturnOptionMethod.EXCHANGE,
  ReturnOptionMethod.STORE_CREDIT,
  ReturnOptionMethod.REFUND,
];

function returnOptionMethod(
  value: ReturnOptionMethod["type"] | undefined,
  t: (key: string) => string,
): string | undefined {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (value) {
    case ReturnOptionMethod.STORE_CREDIT:
      return t("Store credit");
    case ReturnOptionMethod.EXCHANGE:
      return t("Exchange");
    case ReturnOptionMethod.REFUND:
      return t("Refund");
  }
  return undefined;
}

type AdjustmentType = "flat" | "percent";
type AdjustmentCategory = "bonus" | "fee";

interface AdjustmentCategoryData {
  category: AdjustmentCategory;
  label: string;
}

function adjustmentCategoryOptions(
  t: (key: string) => string,
): RedoListItem<AdjustmentCategoryData>[] {
  return [
    {
      type: "text",
      id: "bonus",
      text: t("Bonus"),
      value: { category: "bonus", label: t("Bonus") },
    },
    {
      type: "text",
      id: "fee",
      text: t("Fee"),
      value: { category: "fee", label: t("Fee") },
    },
  ];
}

export const AdjustmentElement = memo(function AdjustmentElement({
  adjustmentFlat,
  adjustmentPercent,
  handleAdjustmentFlat,
  handleAdjustmentPercent,
  handleAmountsChange,
  canEditReturnsSettings,
  currencyOverrides,
  handleCurrencyOverridesChange,
  perCurrencyAmountsEnabled,
  handlePerCurrencyAmountsEnabledChange,
}: {
  adjustmentFlat: string;
  adjustmentPercent: string;
  handleAdjustmentFlat(value: string): void;
  handleAdjustmentPercent(value: string): void;
  handleAmountsChange(next: AdjustmentAmounts): void;
  canEditReturnsSettings: boolean;
  currencyOverrides?: Record<string, string>;
  handleCurrencyOverridesChange?: (overrides: Record<string, string>) => void;
  perCurrencyAmountsEnabled?: boolean;
  handlePerCurrencyAmountsEnabledChange?: (enabled: boolean) => void;
}) {
  const { t } = useTranslation();
  const categoryOptions = adjustmentCategoryOptions(t);
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(() => {
    if (adjustmentPercent !== "0") {
      return "percent";
    }
    return "flat";
  });

  const currentValue =
    adjustmentType === "flat" ? adjustmentFlat : adjustmentPercent;
  const isNegative = parseFloat(currentValue) < 0;
  const adjustmentCategory: AdjustmentCategory = isNegative ? "fee" : "bonus";

  const formatAdjustmentFlat = useHandler((value: string) => {
    handleAdjustmentFlat((+value).toFixed(2));
  });
  const formatAdjustmentPercent = useHandler((value: string) => {
    if (isNaN(parseFloat(value))) {
      handleAdjustmentPercent("0");
    }
  });

  const handleValueChange =
    adjustmentType === "flat" ? handleAdjustmentFlat : handleAdjustmentPercent;
  const formatValue =
    adjustmentType === "flat" ? formatAdjustmentFlat : formatAdjustmentPercent;

  const selectedCategory =
    categoryOptions.find((opt) => opt.value.category === adjustmentCategory) ||
    categoryOptions[0];

  const handleCategoryChange = useHandler((category: AdjustmentCategory) => {
    const numValue = parseFloat(currentValue);
    if (isNaN(numValue)) {
      return;
    }

    const absValue = Math.abs(numValue);
    const newValue = category === "fee" ? -absValue : absValue;

    if (adjustmentType === "flat") {
      handleAdjustmentFlat(newValue.toFixed(2));
    } else {
      handleAdjustmentPercent(String(newValue));
    }
  });

  const displayValue = Math.abs(parseFloat(currentValue) || 0).toString();

  const unitsConflict = hasConflictingAdjustmentUnits({
    adjustmentFlat,
    adjustmentPercent,
  });

  const handleDisplayValueChange = useHandler((value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      handleValueChange("0");
      return;
    }

    const signedValue =
      adjustmentCategory === "fee" ? -Math.abs(numValue) : Math.abs(numValue);

    if (adjustmentType === "flat") {
      handleAdjustmentFlat(signedValue.toFixed(2));
    } else {
      handleAdjustmentPercent(String(signedValue));
    }
  });

  const handleAmountTypeChange = useHandler((type: AmountType) => {
    const toUnit = type === "fixed" ? "flat" : "percent";
    const next = adjustmentAmountsForUnit({
      displayedValue: currentValue,
      toUnit,
      perCurrencyAmountsEnabled,
    });
    setAdjustmentType(toUnit);
    // Both fields change together, so this must be one state update. Queuing
    // two in a tick loses one and closes the inspector.
    handleAmountsChange(next);
  });

  return (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        label={t("Adjustment type")}
        options={categoryOptions}
        optionSelected={({ item }) => handleCategoryChange(item.value.category)}
        selectedItem={selectedCategory}
        size="xs"
      />
      <RedoTextInput
        errorText={
          isNaN(parseFloat(displayValue)) ? t("Invalid number") : undefined
        }
        fullWidth
        inputTypeHint="number"
        label={t("Adjustment amount")}
        onBlur={() => formatValue(currentValue)}
        setValue={handleDisplayValueChange}
        state={canEditReturnsSettings ? "default" : "readonly"}
        trailingActions={
          <AmountTypeDropdown
            amountType={adjustmentType === "flat" ? "fixed" : "percentage"}
            disabled={!canEditReturnsSettings}
            setAmountType={handleAmountTypeChange}
          />
        }
        value={displayValue}
      />
      {unitsConflict && (
        <RedoAlert
          color="warning"
          description={t(
            "This adjustment is set to both {{flat}} and {{percent}}%, and customers are charged both. Pick an amount type to keep only that one.",
            {
              flat: formatFlatAmount(adjustmentFlat),
              percent: adjustmentPercent,
            },
          )}
          fullWidth
          size="sm"
          title={t("Two amounts are active")}
        />
      )}
      {adjustmentType === "flat" &&
        handleCurrencyOverridesChange &&
        handlePerCurrencyAmountsEnabledChange && (
          <PerCurrencyOverrides
            baseAmount={adjustmentFlat}
            currencyOverrides={currencyOverrides ?? {}}
            disabled={!canEditReturnsSettings}
            onCurrencyOverridesChange={handleCurrencyOverridesChange}
            onPerCurrencyAmountsEnabledChange={
              handlePerCurrencyAmountsEnabledChange
            }
            perCurrencyAmountsEnabled={perCurrencyAmountsEnabled ?? false}
          />
        )}
    </Flex>
  );
});

export const ReturnDetails = memo(function ReturnDetails({
  state,
  setState,
  allSteps,
  step,
  setStateWithId,
  flowType,
}: StepTypeDetailsProps<ReturnState>) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  // A read-only-structure flow (e.g. withdrawals) disables every field except
  // explicitly whitelisted return-step controls.
  const canEditReturnsSettings = useFieldEditable(
    useHasPermission(SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS),
  );

  // Contexts required to be defined
  if (!team) {
    return null;
  }

  const getOptionForType = (type: ReturnOptionMethod["type"]) => {
    return state.options.find((opt) => opt.type === type);
  };

  const isOptionEnabled = (type: ReturnOptionMethod["type"]) => {
    return !!getOptionForType(type);
  };

  const allReturnTypes: ReturnOptionMethod["type"][] = [
    ReturnOptionMethod.EXCHANGE,
    ReturnOptionMethod.STORE_CREDIT,
    ReturnOptionMethod.REFUND,
  ];

  const hasAtLeastOneEnabled = allReturnTypes.some((type) =>
    isOptionEnabled(type),
  );

  return (
    <>
      <Flex dir="column" gap="xl">
        {allReturnTypes.map((type, index) => (
          <StateOptionComponent
            canEditReturnsSettings={canEditReturnsSettings}
            isEnabled={isOptionEnabled(type)}
            key={index}
            option={getOptionForType(type)}
            optionType={type}
            setState={setState}
            state={state}
            team={team}
          />
        ))}
      </Flex>
      {!hasAtLeastOneEnabled && (
        <RequiredMessage>
          {t("Must have at least one option enabled")}
        </RequiredMessage>
      )}
      <Divider />
      <AdvancedReturnOptions
        allSteps={allSteps}
        flowType={flowType}
        setState={setState}
        setStateWithId={setStateWithId}
        state={state}
        step={step}
      />
    </>
  );
});

const StateOptionComponent = memo(function StateOptionComponent({
  team,
  state,
  setState,
  canEditReturnsSettings,
  optionType,
  option,
  isEnabled,
}: {
  team: TeamWith<{
    exchanges: Team["settings"]["exchanges"];
    marketing?: Team["settings"]["marketing"];
  }>;
  state: StepTypeDetailsProps<ReturnState>["state"];
  setState: StepTypeDetailsProps<ReturnState>["setState"];
  canEditReturnsSettings: boolean;
  optionType: ReturnOptionMethod["type"];
  option: ReturnState["options"][number] | undefined;
  isEnabled: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { productsState, selectedProduct, onSelectedProductChange } =
    useProductAutocomplete(team._id, option?.variantExchangeAddOnVariantId);

  const canEditGreenReturn = useFieldEditable(
    canEditReturnsSettings,
    "greenReturn",
  );
  const canEditManualLabel = useFieldEditable(
    canEditReturnsSettings,
    "manualLabel",
  );
  const canEditSafetyNet = useFieldEditable(
    canEditReturnsSettings,
    "safetyNet",
  );

  const getIndex = () =>
    state.options.findIndex((opt) => opt.type === optionType);

  const handleToggle = useHandler((enabled: boolean) => {
    setState((state) =>
      produce(state, (draft) => {
        if (enabled) {
          draft.options.push({
            type: optionType,
            creditType: StoreCreditType.GIFT_CARD,
            labelPayer: Payer.Customer,
            ship: true,
            manual_label: false,
            exchangeTypes: {
              sameItem: true,
              variant: true,
              exchangeGroups: true,
              advanced: true,
            },
            chargeCustomerForNewOrderShipping: false,
            adjustmentFlat: "0",
            adjustmentPercent: "0",
            adjustmentType: "flat",
            refundPointsRedeemedForItem: false,
            deductEarnedPointsFromRefund: false,
            reimburseForFinalSale: true,
          });
        } else {
          const index = draft.options.findIndex(
            (opt) => opt.type === optionType,
          );
          if (index !== -1) {
            draft.options.splice(index, 1);
          }
        }
      }),
    );
  });

  const handleAmountsChange = useHandler((next: AdjustmentAmounts) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (!option) {
          return;
        }
        option.adjustmentFlat = next.adjustmentFlat;
        option.adjustmentPercent = next.adjustmentPercent;
        option.perCurrencyAmountsEnabled = next.perCurrencyAmountsEnabled;
      }),
    );
  });

  const handleAdjustmentFlat = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (!option) {
          return;
        }
        option.adjustmentFlat = value;
        option.adjustmentPercent = "0";
        if (parseFloat(value) < 0) {
          option.isUseItOrLoseIt = false;
        }
      }),
    );
  });

  const handleAdjustmentPercent = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (!option) {
          return;
        }
        option.adjustmentPercent = value;
        option.adjustmentFlat = "0";
        if (parseFloat(value) < 0) {
          option.isUseItOrLoseIt = false;
        }
      }),
    );
  });

  const handleCurrencyOverrides = useHandler(
    (overrides: Record<string, string>) => {
      const index = getIndex();
      if (index === -1) {
        return;
      }
      setState((state) =>
        produce(state, (state) => {
          const option = state.options[index];
          if (option) {
            option.currencyOverrides = overrides;
          }
        }),
      );
    },
  );

  const handlePerCurrencyAmountsEnabled = useHandler((enabled: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.perCurrencyAmountsEnabled = enabled;
        }
      }),
    );
  });

  const handleGreenReturn = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.ship = !value;
        }
      }),
    );
  });

  const handleManualLabel = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (!option) {
          return;
        }
        option.manual_label = value;
        if (value) {
          option.ship = true;
          option.labelPayer = Payer.Customer;
        }
      }),
    );
  });

  const handleSafetyNet = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.labelPayer = value ? Payer.Merchant : Payer.Customer;
        }
      }),
    );
  });

  const handleExchangeTypeSameItem = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option?.exchangeTypes) {
          option.exchangeTypes.sameItem = value;
        }
      }),
    );
  });

  const handleExchangeTypeVariant = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option?.exchangeTypes) {
          option.exchangeTypes.variant = value;
        }
      }),
    );
  });

  const handleExchangeTypeExchangeGroups = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option?.exchangeTypes) {
          option.exchangeTypes.exchangeGroups = value;
        }
      }),
    );
  });

  const handleExchangeTypeAdvanced = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (!option?.exchangeTypes) {
          return;
        }
        option.exchangeTypes.advanced = value;
        if (!value) {
          option.isUseItOrLoseIt = false;
        }
      }),
    );
  });

  const handleVariantExchangeVariantId = useHandler(
    (value: ProductVariantInfo | null | undefined) => {
      const index = getIndex();
      if (index === -1) {
        return;
      }
      onSelectedProductChange(value ?? null);
      if (value) {
        setState((state) =>
          produce(state, (state) => {
            const option = state.options[index];
            if (option) {
              option.variantExchangeAddOnVariantId = value.variantId;
            }
          }),
        );
      }
    },
  );

  const handleVariantExchangeAddOnTitle = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.variantExchangeAddOnTitle = value;
        }
      }),
    );
  });

  const handleVariantExchangeAddOnAcceptButton = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.variantExchangeAddOnAcceptButton = value;
        }
      }),
    );
  });

  const handleVariantExchangeAddOnRejectButton = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.variantExchangeAddOnRejectButton = value;
        }
      }),
    );
  });

  const handleExchangeShipping = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.chargeCustomerForNewOrderShipping = value;
        }
      }),
    );
  });

  const handleCarryOverLineItemProperties = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (!option) {
          return;
        }
        option.carryOverLineItemProperties = value;
      }),
    );
  });

  const handleCarryOverLineItemPropertiesOnAddOnAccept = useHandler(
    (value: boolean) => {
      const index = getIndex();
      if (index === -1) {
        return;
      }
      setState((state) =>
        produce(state, (state) => {
          const option = state.options[index];
          if (!option) {
            return;
          }
          option.carryOverLineItemPropertiesOnAddOnAccept = value;
        }),
      );
    },
  );

  const handleRefundPointsRedeemed = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.refundPointsRedeemedForItem = value;
        }
      }),
    );
  });

  const handleDeductEarnedPoints = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.deductEarnedPointsFromRefund = value;
        }
      }),
    );
  });

  const handleReimburseForFinalSale = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.reimburseForFinalSale = value;
        }
      }),
    );
  });

  const handleIsUseItOrLoseIt = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.isUseItOrLoseIt = value;
        }
      }),
    );
  });

  const handleOverrideAdjustmentWithCode = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) {
      return;
    }
    setState((state) =>
      produce(state, (state) => {
        const stateOption = state.options[index];
        if (stateOption) {
          stateOption.overrideAdjustmentWithCode = value;
        }
      }),
    );
  });
  if (!isEnabled) {
    return (
      <Flex className={returnCss.optionCard} dir="column" gap="md">
        <Flex align="center" dir="row" justify="space-between">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {returnOptionMethod(optionType, t)}
          </Text>
          <RedoToggle
            disabled={!canEditReturnsSettings}
            setValue={handleToggle}
            size="xs"
            value={false}
          />
        </Flex>
      </Flex>
    );
  }

  const isBonus =
    isBonusAdjustment(option?.adjustmentFlat || "0") &&
    isBonusAdjustment(option?.adjustmentPercent || "0");
  const optionHasAdvancedExchanges =
    optionType === ReturnOptionMethod.EXCHANGE &&
    option?.exchangeTypes?.advanced;

  return (
    <Flex className={returnCss.optionCard} dir="column" gap="md">
      <Flex align="center" dir="row" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {returnOptionMethod(optionType, t)}
        </Text>
        <RedoToggle
          disabled={!canEditReturnsSettings}
          setValue={handleToggle}
          size="xs"
          value
        />
      </Flex>
      <AdjustmentElement
        adjustmentFlat={option?.adjustmentFlat || "0"}
        adjustmentPercent={option?.adjustmentPercent || "0"}
        canEditReturnsSettings={
          canEditReturnsSettings && !option?.overrideAdjustmentWithCode
        }
        currencyOverrides={option?.currencyOverrides}
        handleAdjustmentFlat={handleAdjustmentFlat}
        handleAdjustmentPercent={handleAdjustmentPercent}
        handleAmountsChange={handleAmountsChange}
        handleCurrencyOverridesChange={handleCurrencyOverrides}
        handlePerCurrencyAmountsEnabledChange={handlePerCurrencyAmountsEnabled}
        perCurrencyAmountsEnabled={option?.perCurrencyAmountsEnabled}
      />
      {option?.overrideAdjustmentWithCode && (
        <Text fontSize="xs" textColor="secondary">
          <Link
            to={routes.getSettingsSubRoute(RETURNS_AUTOMATIONS_SETTINGS_PATH)}
          >
            {t("Computed by the return adjustment function.")}
          </Link>
        </Text>
      )}
      {optionHasAdvancedExchanges && isBonus && (
        <Flex align="center" gap="xs">
          <RedoToggle
            disabled={
              !canEditReturnsSettings || !!option?.overrideAdjustmentWithCode
            }
            label={t("Use it or lose it")}
            setValue={handleIsUseItOrLoseIt}
            size="xs"
            value={option?.isUseItOrLoseIt ?? false}
          />
          <InfoTooltip
            boxSize={16}
            tooltip={{
              title: t(
                "If enabled, any excess bonus not applied to the exchange will be lost rather than rolling over as store credit or refund.",
              ),
            }}
          />
        </Flex>
      )}
      <RedoCollapseSubsection title={t("Advanced options")}>
        <RedoToggle
          description={t("Customer does not send item back.")}
          disabled={!canEditGreenReturn}
          label={t("Green return")}
          setValue={handleGreenReturn}
          size="xs"
          value={!option!.ship} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        />
        <RedoToggle
          description={t(
            "Merchant pays for labels not covered by {{brandName}}.",
            { brandName: "Redo" },
          )}
          disabled={!canEditSafetyNet}
          label={t("Safety net")}
          setValue={handleSafetyNet}
          size="xs"
          value={option!.labelPayer === Payer.Merchant} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        />
        <RedoToggle
          description={t("Merchant will handle the shipping label")}
          disabled={!canEditManualLabel}
          label={t("Manual Label")}
          setValue={handleManualLabel}
          size="xs"
          value={option!.manual_label ?? false} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        />
        <RedoToggle
          description={t(
            "Controls whether merchants are reimbursed for final sale returns processed through this option",
          )}
          disabled={!canEditReturnsSettings}
          label={t("Reimburse for final sale")}
          setValue={handleReimburseForFinalSale}
          size="xs"
          value={option!.reimburseForFinalSale ?? true} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        />
        <RedoToggle
          description={t(
            "Use custom code to compute adjustments for {{method}}.",
            {
              method: (
                returnOptionMethod(optionType, t) ?? t("this method")
              ).toLowerCase(),
            },
          )}
          disabled={!canEditReturnsSettings}
          label={t("Override adjustment with code")}
          setValue={handleOverrideAdjustmentWithCode}
          size="xs"
          value={option!.overrideAdjustmentWithCode ?? false} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        />
        {optionType === ReturnOptionMethod.EXCHANGE && (
          <>
            <Flex dir="column" gap="sm">
              <Flex dir="column" gap="xxs">
                <Text fontSize="sm" fontWeight="medium" textColor="primary">
                  {t("Allow exchange types")}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {t("Select what types of exchanges to allow.")}
                </Text>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                {option!.exchangeTypes!.advanced &&
                  team.settings.exchanges.advancedExchanges ===
                    AdvancedExchanges.DISABLED && (
                    <Text fontSize="xs" textColor="secondary">
                      {t(
                        "⚠ Advanced exchanges are disabled globally in your Exchanges settings.",
                      )}
                    </Text>
                  )}
              </Flex>
              <Flex dir="column" gap="xs">
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label={t("Same item")}
                  setValue={(v) => handleExchangeTypeSameItem(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.sameItem} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                />
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label={t("Variant items")}
                  setValue={(v) => handleExchangeTypeVariant(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.variant} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                />
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label={t("Exchange groups")}
                  setValue={(v) => handleExchangeTypeExchangeGroups(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.exchangeGroups} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                />
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label={t("Any item")}
                  setValue={(v) => handleExchangeTypeAdvanced(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.advanced} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                />
              </Flex>
            </Flex>
            <RedoToggle
              description={t(
                "If enabled, line item properties (e.g. engravings, monograms) from the returned item are copied to the same-item or variant exchange item.",
              )}
              disabled={!canEditReturnsSettings}
              label={t("Carry over item properties to exchanges")}
              setValue={handleCarryOverLineItemProperties}
              size="xs"
              value={option?.carryOverLineItemProperties ?? true}
            />
            <RedoToggle
              description={t(
                "Offer the customer an add-on product if they do variant or same-item exchange. The customer will be charged the cost of the product.",
              )}
              disabled={!canEditReturnsSettings}
              label={t("Variant exchange add-on")}
              setValue={(value) => {
                const index = getIndex();
                if (index === -1) {
                  return;
                }
                setState((state) =>
                  produce(state, (state) => {
                    const option = state.options[index];
                    if (option) {
                      option.variantExchangeAddOn = value;
                    }
                  }),
                );
              }}
              size="xs"
              value={option!.variantExchangeAddOn ?? false} // eslint-disable-line @typescript-eslint/no-non-null-assertion
            />
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            {option!.variantExchangeAddOn && (
              <Flex dir="column" gap="lg" pl="lg">
                <ProductAutocomplete
                  label={t("Product to add to exchange")}
                  productsState={productsState}
                  setValue={handleVariantExchangeVariantId}
                  value={selectedProduct}
                />
                <RedoTextInput
                  label={t("Title text")}
                  setValue={handleVariantExchangeAddOnTitle}
                  value={option?.variantExchangeAddOnTitle ?? ""}
                />
                <Flex dir="row" gap="md">
                  <RedoTextInput
                    fullWidth
                    label={t("Accept text")}
                    placeholder={t("Yes")}
                    setValue={handleVariantExchangeAddOnAcceptButton}
                    value={option?.variantExchangeAddOnAcceptButton ?? ""}
                  />
                  <RedoTextInput
                    fullWidth
                    label={t("Reject text")}
                    placeholder={t("No")}
                    setValue={handleVariantExchangeAddOnRejectButton}
                    value={option?.variantExchangeAddOnRejectButton ?? ""}
                  />
                </Flex>
                {(option?.carryOverLineItemProperties ?? true) && (
                  <RedoToggle
                    description={t(
                      "If enabled, line item properties (e.g. engravings, monograms) are copied only when the customer accepts the add-on. Rejecting the add-on does not copy them.",
                    )}
                    disabled={!canEditReturnsSettings}
                    label={t("Carry over item properties only if they accept")}
                    setValue={handleCarryOverLineItemPropertiesOnAddOnAccept}
                    size="xs"
                    value={
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      option!.carryOverLineItemPropertiesOnAddOnAccept ?? false
                    }
                  />
                )}
              </Flex>
            )}
            <RedoToggle
              description={t(
                "If enabled, customer will be charged for outgoing shipping of exchange orders.",
              )}
              disabled={!canEditReturnsSettings}
              label={t("Charge customer for new order shipping")}
              setValue={handleExchangeShipping}
              size="xs"
              value={option!.chargeCustomerForNewOrderShipping ?? false} // eslint-disable-line @typescript-eslint/no-non-null-assertion
            />
          </>
        )}
      </RedoCollapseSubsection>
      {team.settings.marketing?.loyalty?.enabled && (
        <CollapseSubsection title={t("Loyalty options")}>
          <RedoToggle
            description={t(
              "Refund points that were redeemed for this specific item",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Refund points redeemed for this item")}
            setValue={handleRefundPointsRedeemed}
            size="xs"
            value={option!.refundPointsRedeemedForItem ?? false} // eslint-disable-line @typescript-eslint/no-non-null-assertion
          />
          <RedoToggle
            description={t(
              "Deduct loyalty points earned from this purchase from the refund amount",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Deduct earned points from refund")}
            setValue={handleDeductEarnedPoints}
            size="xs"
            value={option!.deductEarnedPointsFromRefund ?? false} // eslint-disable-line @typescript-eslint/no-non-null-assertion
          />
        </CollapseSubsection>
      )}
    </Flex>
  );
});
